import { useState, useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DriveSidebar from 'components/layout/DriveSidebar'
import { api } from 'roadie-ui'
import { fetchTaxSummary } from 'actions/taxes'
import { Redirect, useParams } from 'react-router-dom'
import Panel from 'components/layout/Panel'
import Page from 'rui/widgets/Page'
import { Spacer, Button, Icon, Text, Container, Loader } from 'roadie-ui'
import { useLocalization } from 'localization'

type Downloads = {
  mileage?: string
  federal_1099?: string
}

type FileType = 'earning' | 'seattleEarning' | 'consolidationSeattleStatement' | 'mileage' | '1099'

type Earning = {
  id: number
  profile_id: number
  statement_period: { title: string; start_time: string; end_time: string }
}

type Taxes = {
  downloads: Downloads
  year: string
}

const Content = (props: { fileType: FileType; year: string; itemId: string; downloads: Downloads }) => {
  const { fileType, year, itemId, downloads } = props
  const { t, formatStatementPeriodTitle } = useLocalization()

  const [loading, setLoading] = useState(false)

  const earnings = useSelector(
    (state: { prop22EarningsStatement: Array<Earning>; seattleEarningsStatement: Array<Earning> }) => {
      return [...(state.prop22EarningsStatement || []), ...(state.seattleEarningsStatement || [])]
    }
  )
  const earning = earnings.find((item: Earning) => item.id === Number(year))

  const captions = {
    earning: t('dweb_download_earnings_statement'),
    seattleEarning: t('dweb_download_seattle_earnings_statement'),
    consolidationSeattleStatement: t('dweb_download_consolidation_seattle_statement'),
    mileage: t('dweb_download_tax_document'),
    1099: t('dweb_download_tax_document'),
  }

  const formSubtitles = useMemo(
    () => ({
      earning: t('dweb_download_ca_earning_statement'),
      seattleEarning: t('dweb_download_seattle_earning_statement'),
      consolidationSeattleStatement: t('dweb_download_consolidation_seattle_statement_title'),
      mileage: t('dweb_tax_info_roadie_mileage_log'),
      1099: t('dweb_tax_info_form_1099', { formType: +year < 2020 ? 'MISC' : 'NEC' }),
    }),
    [t, year]
  )

  const yearSubtitles = useMemo(
    () => ({
      earning: earning && formatStatementPeriodTitle(earning.statement_period),
      seattleEarning: earning && formatStatementPeriodTitle(earning.statement_period),
      consolidationSeattleStatement: '',
      mileage: t('dweb_common_year_value1', { value1: year }),
      1099: t('dweb_common_year_value1', { value1: year }),
    }),
    [earning, formatStatementPeriodTitle, t, year]
  )

  const paths = useMemo(
    () => ({
      earning: `/my_profile/earnings_statements/${itemId}/download`,
      seattleEarning: `/my_profile/earnings_statements/${itemId}/download`,
      consolidationSeattleStatement: `consolidations/${itemId}/download_statement`,
      mileage: downloads && downloads.mileage,
      1099: downloads && downloads.federal_1099,
    }),
    [downloads, itemId]
  )

  const names = useMemo(
    () => ({
      earning: 'earnings.pdf',
      seattleEarning: `weeklyStatement${earning && earning.statement_period.title}.pdf`,
      consolidationSeattleStatement: 'consolidationStatement.pdf',
      mileage: `${year}_Roadie_Mileage_Log.csv`,
      1099: `${year}_Roadie_1099.pdf`,
    }),
    [earning, year]
  )

  const buttonNames = {
    earning: 'Download PDF',
    seattleEarning: 'Download PDF',
    consolidationSeattleStatement: 'Download PDF',
    mileage: 'Download CSV',
    1099: 'Download PDF',
  }

  const caption = captions[fileType]
  const formSubtitle = formSubtitles[fileType]
  const yearSubtitle = yearSubtitles[fileType]
  const path = paths[fileType]
  const name = names[fileType]
  const buttonName = buttonNames[fileType] || 'Download PDF'

  const handleDownload = useCallback(() => {
    if (path) {
      setLoading(true)
      api.downloadFile({ path, name }).then(() => setLoading(false))
    }
  }, [path, name])

  return (
    <div>
      <Text.Overline>{caption}</Text.Overline>
      <Spacer $height="2.1rem" />
      <Container.Grid $gridTemplateColumns="2.4rem auto" $gridGap=".8rem" $justifyContent="center" $alignItems="center">
        <Icon.Document />
        <Text.H6 $color="neutral-black">{formSubtitle}</Text.H6>
      </Container.Grid>
      <Spacer $height=".8rem" />
      <Text.Subtitle1>{yearSubtitle}</Text.Subtitle1>
      <Spacer $height="3.5rem" />
      <Container.Grid $justifyContent="center">
        {loading ? (
          <Loader size="4rem" />
        ) : (
          <Button.Primary $width="25.6rem" $height="4rem" onClick={handleDownload}>
            {buttonName}
          </Button.Primary>
        )}
      </Container.Grid>
    </div>
  )
}

const DownloadPage = () => {
  const { fileType, itemId } = useParams<{ fileType: FileType; itemId: string }>()

  const currentYear = new Date().getFullYear()

  const dispatch = useDispatch()
  const taxes = useSelector((state: { taxes: Taxes }) => state.taxes || {})

  const loadSummary = useCallback(
    (itemId: string) => {
      dispatch(fetchTaxSummary(itemId))
    },
    [dispatch]
  )

  const isTaxForm = fileType == 'mileage' || fileType == '1099'
  const isValidTaxForm = isTaxForm && Number(itemId) >= 2013 && Number(itemId) <= currentYear

  useEffect(() => {
    if (isValidTaxForm) {
      loadSummary(itemId)
    }
  }, [isValidTaxForm, itemId, loadSummary])

  // Redirect to /dashboard if the year is not valid for a tax form (i.e. 'mileage' or '1099' file type)
  if (isTaxForm && !isValidTaxForm) {
    return <Redirect to={{ pathname: '/dashboard' }} />
  }

  return (
    <Page sidebar={<DriveSidebar />}>
      <Panel>
        <Container.Grid $justifyContent="center" $textAlign="center" $justifyItems="center">
          <Spacer $height="14.9rem" />
          <Container.Grid
            $justifyContent="center"
            $alignItems="center"
            $borderRadius="50%"
            $backgroundColor="neutral-lightest"
            $width="14rem"
            $height="14rem"
          >
            <Icon.DocumentFinancial $width="7rem" $height="7rem" $color="neutral-light" />
          </Container.Grid>
          <Spacer $height="4.8rem" />
          <Content fileType={fileType} year={itemId} itemId={itemId} downloads={taxes.downloads} />
          <Spacer $height="4.8rem" />
        </Container.Grid>
      </Panel>
    </Page>
  )
}

export default DownloadPage
